import React, { useEffect, useRef, useState } from "react";
import { Form, Formik, useFormikContext } from "formik";
import { applySpec, find, map, path, pick, pipe, prop, propEq, propOr } from "ramda";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  Contact,
  Mutation,
  MutationCreatePaymentArgs,
  MutationCreatePaymentIntentArgs,
  Query,
  QueryGetContactArgs,
  QueryGetJobArgs,
  QueryGetPaymentsPaginatedArgs,
} from "../../generated/nest-graphql";
import * as Yup from "yup";
import { CREATE_PAYMENT } from "../../graphql/mutations/createPayment";
import { showErrorAlert, showSuccessAlert, showWarningAlert } from "../../actions";
import { parseTaxGateError, taxGateMessage, TaxGateOutcome } from "../../lib/taxGate";
import { useDispatch } from "../../contexts/snackbar-context";
import { Button, CircularProgress, Link, Typography } from "@material-ui/core";
import { CancelButton } from "../Buttons/CancelButton";
import { SubmitButton } from "../Buttons/SubmitButton";
import { CheckboxField } from "./fields/CheckboxField";
import currency from "currency.js";
import { GET_CONTACT_DETAILS } from "../../graphql/queries/getContactDetails";
import { AddPaymentOptionsDialog } from "../Payments/AddPaymentOptionsDialog";
import { CREATE_CONFIRMED_PAYMENT_INTENT } from "../../graphql/mutations/createConfirmedPaymentIntent";
import { hashIdempotencyKey } from "../../lib/functions";
import * as Sentry from "@sentry/react";
import PaymentMethodsForm from "./PaymentMethodsForm";
import { BuyNowPayLaterDialog } from "../Payments/BuyNowPayLaterDialog";
import { AreYouSureDialog } from "../Items/AreYouSureDialog";
import { GET_JOB } from "../../graphql/queries/getJob";
import { useHistory } from "react-router-dom";
import { GET_PAYMENTS_PAGINATED } from "../../graphql/queries/getPaymentsPaginated";
import { useAxiosClient } from "../../lib/nestBackendClient";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";

type PaymentValues = {
  paymentMethod: string;
  type: string;
  receivedDate: Date;
  amount: string;
  refNumber?: string;
  memo?: string;
  payer: string;
  source: string;
  paymentIntentId?: string;
};

type PaymentMethodCollection = {
  label: string;
  value: string;
  default: boolean;
  paymentMethod: any;
};

export type AddPaymentFormValues = {
  payments: PaymentValues[];
  sendReceipt: boolean;
};

const validationSchema = Yup.object().shape({
  payments: Yup.array().of(
    Yup.object().shape({
      payer: Yup.string().required("Required").min(1),
      amount: Yup.string().required("Required").min(1).notOneOf(["0.00"], "Amount must be greater than 0"),
      paymentMethod: Yup.string().required("Required").min(1),
    })
  ),
});

// ~2 minutes of 3s checks, counted in ticks so a backgrounded tab (timers paused) doesn't use them up.
const MAX_PAYMENT_POLL_TICKS = 40;

export type PaymentDialogState = {
  inProgress: boolean;
  // The balance on screen may be out of date (part of a split succeeded, or a tax correction didn't load).
  balanceMayBeStale: boolean;
};

// Lets the dialog's X ask before abandoning a payment; resets on unmount.
const PaymentStateReporter: React.FC<{
  inProgress: boolean;
  balanceMayBeStale: boolean;
  onChange?: (state: PaymentDialogState) => void;
}> = ({ inProgress, balanceMayBeStale, onChange }) => {
  useEffect(() => {
    onChange?.({ inProgress, balanceMayBeStale });
  }, [inProgress, balanceMayBeStale, onChange]);
  useEffect(() => () => onChange?.({ inProgress: false, balanceMayBeStale: false }), [onChange]);
  return null;
};

// A single payment's amount is read-only, so it follows balance changes (e.g. a tax correction).
// Split rows are left as entered (only reachable before any row has gone through; after a partial payment the
// form closes instead), and the form flags the mismatch and offers "Adjust amount".
const BalanceDueSync: React.FC<{ balanceDue: string; hold: boolean }> = ({ balanceDue, hold }) => {
  const { values, setFieldValue } = useFormikContext<AddPaymentFormValues>();
  const appliedBalance = useRef(balanceDue);
  const rowCount = values.payments.length;
  useEffect(() => {
    if (hold || rowCount !== 1 || appliedBalance.current === balanceDue) return;
    appliedBalance.current = balanceDue;
    setFieldValue("payments.0.amount", balanceDue);
  }, [balanceDue, hold, rowCount, setFieldValue]);
  return null;
};

// No GraphQL response: the server may still have processed the request.
const isNetworkFailure = (error: any) => !!error?.networkError && !(error?.graphQLErrors?.length > 0);

interface ValidationStatusProps {
  payment;
  index: number;
  setSubmitting: (submitting: boolean) => void;
  inInterval: boolean;
  values: PaymentValues[];
  sendReceipt: boolean;
}

interface ValidatePaymentProps {
  paymentId: string;
  index: number;
  setSubmitting: (submitting: boolean) => void;
  values: PaymentValues[];
  sendReceipt: boolean;
  pollId: ReturnType<typeof setInterval>;
}
export const AddPaymentForm: React.FC<{
  amount: string;
  balanceDue: string;
  invoiceId: string;
  contactId: string;
  refetch: any;
  payer: string;
  laborCost: string;
  partsCost: string;
  partsTax: string;
  laborTax: string;
  totalTax: string;
  subTotal: string;
  onClose: () => void;
  jobId: string;
  checkListsCreated: boolean;
  contact: Contact;
  amountPaid: string;
  status: string;
  openInspection: () => void;
  onPaymentStateChange?: (state: PaymentDialogState) => void;
}> = ({
  amount,
  balanceDue,
  invoiceId,
  contactId,
  refetch,
  onClose,
  laborCost,
  partsCost,
  payer,
  partsTax,
  laborTax,
  totalTax,
  subTotal,
  jobId,
  checkListsCreated,
  contact,
  amountPaid,
  openInspection,
  status,
  onPaymentStateChange,
}) => {
  const isPartiallyPaid = Number(amountPaid) > 0; //rather than passing a status just check if the amount paid is greater than 0
  const [successfulPayments, setSuccessfulPayments] = useState<number[]>([]);
  // The payment chain spans awaits and poll ticks, where state would be stale, so it reads a ref.
  const successfulPaymentsRef = useRef<number[]>([]);
  const isOnline = useOnlineStatus();
  const isUnmounted = useRef(false);
  const [paymentStillConfirming, setPaymentStillConfirming] = useState(false);
  // Request lost in transit: outcome unknown, so no new payment until the invoice is refreshed.
  const [paymentUnknown, setPaymentUnknown] = useState(false);
  const [isRefreshingAfterUnknown, setIsRefreshingAfterUnknown] = useState(false);
  // Cancel's pre-close refresh counts as in progress, so nothing can start and be closed underneath.
  const [isRefreshingBeforeClose, setIsRefreshingBeforeClose] = useState(false);
  // A tax correction changed the total but the refresh failed: the amounts on screen are pre-correction, and the
  // backend won't reject them on a retry. Only a successful refresh (tapped by the tech) unlocks the form.
  const [needsBalanceRefresh, setNeedsBalanceRefresh] = useState(false);
  const [isRefreshingBalance, setIsRefreshingBalance] = useState(false);
  // Payment we stopped polling on. Only re-checking it is allowed: the backend doesn't block
  // Cash/Check on top of a processing card charge.
  const unconfirmedPayment = useRef<Omit<ValidationStatusProps, "payment" | "inInterval"> & { paymentId: string }>();
  const [isReadyForBillingLoading, setIsReadyForBillingLoading] = useState(false);
  const nestBackendClient = useAxiosClient();
  const { data, refetch: contactRefetch } = useQuery<Query, QueryGetContactArgs>(GET_CONTACT_DETAILS, {
    variables: {
      id: contactId,
    },
  });
  const {
    data: jobData,
    loading: jobLoading,
    error: jobError,
    refetch: refetchJob,
  } = useQuery<Query, QueryGetJobArgs>(GET_JOB, {
    variables: {
      id: jobId,
    },
    fetchPolicy: "cache-first",
    returnPartialData: true,
  });
  const [isRetryingJob, setIsRetryingJob] = useState(false);
  const [jobRetryFailed, setJobRetryFailed] = useState(false);
  const jobFailureReported = useRef(false);
  // Keep the last loaded job so a cache eviction mid-payment doesn't unmount the form.
  const lastLoadedJob = useRef<Query["getJob"]>();
  if (jobData?.getJob) {
    lastLoadedJob.current = jobData.getJob;
  }
  const contactData = data?.getContact ?? contact;
  const paymentMethods: PaymentMethodCollection[] = pipe(
    prop("stripePaymentMethods"),
    map(
      applySpec({
        label: ({ card }) => `${prop("brand", card)} ${prop("last4", card)}`,
        value: prop<any, string>("id"),
        default: prop<any, boolean>("default"),
        paymentMethod: (pm) => pm,
      })
    )
    // stripePaymentMethods (and its entries) can come back null; either one throws while building the labels.
  )({ ...contactData, stripePaymentMethods: (contactData?.stripePaymentMethods ?? []).filter(Boolean) } as any);
  const cardOptions = map(pick(["label", "value"]))(paymentMethods);
  const [createPayment] = useMutation<Mutation, MutationCreatePaymentArgs>(CREATE_PAYMENT);
  const dispatch = useDispatch();
  const copyCheckoutLink = () => {
    const url = `${process.env.REACT_APP_CUSTOMER_WEB_APP_URL}/checkout?iid=${invoiceId}`;
    navigator.clipboard.writeText(url);
    showSuccessAlert(dispatch, "Checkout link copied to clipboard");
  };
  const [refetchProgress, setRefetchProgress] = useState("");
  const { push } = useHistory();

  const handleAddWarrantyCheckReason = () => {
    onClose();
    push(`/jobs/${jobId}?openJobTypeModal=true`);
  };

  useEffect(() => {
    if (status === "Paid") {
      showSuccessAlert(dispatch, "Customer Payment Successful");
      onClose();
    }
  }, [status, dispatch, onClose]);
  const initialValues = {
    payments: [
      {
        laborCost,
        partsCost,
        partsTax,
        laborTax,
        totalTax,
        subTotal,
        source: "",
        paymentMethod: "",
        type: "Invoice",
        amount: balanceDue,
        receivedDate: new Date(),
        payer,
      },
    ],
    sendReceipt: true,
  };
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [addNewCardState, setAddNewCardState] = useState<{
    showDialog: boolean;
    setPaymentMethod: ((paymentMethodId: string) => void) | null;
  }>({ showDialog: false, setPaymentMethod: null });
  const [buyNowPayLaterState, setBuyNowPayLaterState] = useState<{
    showDialog: boolean;
    setPaymentMethod: (() => void) | null;
    buyNowPayLaterSent: boolean;
  }>({
    showDialog: false,
    setPaymentMethod: null,
    buyNowPayLaterSent: false,
  });
  // In a ref: the poll callback keeps the render it was created in, where state would read empty.
  const paymentPoll = useRef<ReturnType<typeof setInterval>>();
  const [isPollingPayment, setIsPollingPayment] = useState(false);

  const stopPaymentPoll = () => {
    if (paymentPoll.current) {
      clearInterval(paymentPoll.current);
      paymentPoll.current = undefined;
      setIsPollingPayment(false);
    }
  };

  const startPaymentPoll = (tick: (pollId: ReturnType<typeof setInterval>) => void, onGiveUp: () => void) => {
    stopPaymentPoll();
    let ticks = 0;
    const pollId = setInterval(() => {
      ticks += 1;
      if (ticks > MAX_PAYMENT_POLL_TICKS) {
        stopPaymentPoll();
        onGiveUp();
        return;
      }
      tick(pollId);
    }, 3000);
    paymentPoll.current = pollId;
    setIsPollingPayment(true);
  };

  const markPaymentSucceeded = (index: number) => {
    if (!successfulPaymentsRef.current.includes(index)) {
      successfulPaymentsRef.current = [...successfulPaymentsRef.current, index];
      setSuccessfulPayments(successfulPaymentsRef.current);
    }
  };

  const nextUnpaidIndex = (values: PaymentValues[]) =>
    values.findIndex((_payment, i) => !successfulPaymentsRef.current.includes(i));

  function validateStatus({ payment, index, setSubmitting, inInterval, values, sendReceipt }: ValidationStatusProps) {
    // Dialog closed: don't go on to charge the next split payment.
    if (isUnmounted.current) return;
    if (payment?.status === "processing") {
      if (!inInterval) {
        startPaymentPoll(
          (pollId) => validatePayment({ paymentId: payment.id, index, setSubmitting, values, sendReceipt, pollId }),
          () => {
            if (isUnmounted.current) return;
            // Formik stays submitting; Submit is replaced by a re-check.
            unconfirmedPayment.current = { paymentId: payment.id, index, setSubmitting, values, sendReceipt };
            setPaymentStillConfirming(true);
          }
        );
      }
      return;
    }
    stopPaymentPoll();
    if (payment?.status !== "succeeded") {
      showErrorAlert(dispatch, payment?.message ?? "Payment Charge Failed");
      setSubmitting(false);
      return;
    }
    markPaymentSucceeded(index);
    const next = nextUnpaidIndex(values);
    if (next === -1) {
      onSuccessfulFinish(setSubmitting);
      return;
    }
    handleSinglePayment(values, sendReceipt, next, setSubmitting);
  }

  async function validatePayment({
    paymentId,
    index,
    setSubmitting,
    values,
    sendReceipt,
    pollId,
  }: ValidatePaymentProps) {
    let data: Query | undefined;
    try {
      ({ data } = await getPayments({
        variables: {
          paginatedQueryInput: {
            filter: {
              _id: paymentId,
            },
            limit: 1,
            skip: 0,
          },
        },
      }));
    } catch (e) {
      // Network error: outcome unknown, skip this tick.
      return;
    }
    // Ignore a tick from a poll that has since stopped or been replaced.
    if (paymentPoll.current !== pollId) return;
    // No data isn't a failed charge; try again next tick.
    if (!data?.getPaymentsPaginated) return;
    const payment = data.getPaymentsPaginated.edges?.[0];
    validateStatus({ payment, index, setSubmitting, values, sendReceipt, inInterval: true });
  }

  async function tryCardPayment(payment: PaymentValues, sendReceipt: boolean, idx) {
    const paymentMethodId = prop("paymentMethod", payment);
    const { amount: paymentAmount } = payment;
    const percentOfTotalAmount = Number(paymentAmount) / Number(cleanedAmount(balanceDue));
    const toPercentOfTotalAmount = (val) => cleanedAmount(currency(val).multiply(percentOfTotalAmount));
    const paymentMethod: any = pipe(
      find(propEq("value", paymentMethodId)) as any,
      prop("paymentMethod")
    )(paymentMethods);
    if (!paymentMethod) {
      // Selected card is no longer on file; nothing was charged.
      return {
        status: "failed",
        message: "That saved card is no longer on file. Tap Refresh Cards and choose a card again.",
      };
    }
    try {
      //hashing on the last two so that someone can use a card a second time if their other method failed
      const idempotencyKey = await hashIdempotencyKey(`onFile:${invoiceId}:${paymentMethodId}:${idx}:${balanceDue}`);
      const result = await createConfirmedPayment({
        variables: {
          createPaymentInput: {
            type: "Invoice",
            paymentMethod: "Credit Card",
            status: "processing",
            invoice: invoiceId,
            laborCost: toPercentOfTotalAmount(laborCost),
            partsCost: toPercentOfTotalAmount(partsCost),
            partsTax: toPercentOfTotalAmount(partsTax),
            laborTax: toPercentOfTotalAmount(laborTax),
            totalTax: toPercentOfTotalAmount(totalTax),
            subTotal: toPercentOfTotalAmount(subTotal),
            contact: contact.id,
            customer: path(["stripeCustomer", "id"], contact),
            stripePaymentMethod: paymentMethod["id"],
            amount: paymentAmount,
            invoicePrice: paymentAmount,
            payer: `${contact.firstName} ${contact.lastName}`,
            sendReceipt,
            source: "stored stripe payment methods",
            receivedDate: new Date(),
          },
          idempotencyKey,
        },
      });
      return result.data?.createConfirmedPaymentIntent;
    } catch (e) {
      Sentry.captureException(propOr("", "message", e));
      throw e;
    }
  }

  const tryCashCheckPayment = async (payment: PaymentValues, sendReceipt: boolean, idx: number) => {
    const { amount: paymentAmount } = payment;
    const percentOfTotalAmount = Number(paymentAmount) / Number(cleanedAmount(balanceDue));
    const toPercentOfTotalAmount = (val) => cleanedAmount(currency(val).multiply(percentOfTotalAmount));
    try {
      const result = await createPayment({
        variables: {
          createPaymentInput: {
            paymentIntentId: payment?.paymentIntentId ?? "",
            amount: cleanedAmount(paymentAmount),
            invoicePrice: cleanedAmount(paymentAmount),
            payer: payer || "testPayer",
            paymentMethod: payment?.paymentMethod,
            receivedDate: payment?.receivedDate ?? null,
            laborCost: toPercentOfTotalAmount(laborCost),
            partsCost: toPercentOfTotalAmount(partsCost),
            partsTax: toPercentOfTotalAmount(partsTax),
            laborTax: toPercentOfTotalAmount(laborTax),
            totalTax: toPercentOfTotalAmount(totalTax),
            subTotal: toPercentOfTotalAmount(subTotal),
            type: payment.type,
            status: "succeeded",
            source: payment?.source ?? "",
            invoice: invoiceId,
            contact: contactId,
            memo: payment?.memo ?? "",
            refNumber: payment?.refNumber ?? "",
            sendReceipt,
          },
        },
      });
      return result.data?.createPayment;
    } catch (e) {
      Sentry.captureException(propOr("", "message", e));
      // Preserve a tax-gate error so handleSinglePayment can react; otherwise keep the
      // existing multi-payment contract of throwing the index.
      if (parseTaxGateError(e) || isNetworkFailure(e)) throw e;
      throw idx;
    }
  };

  useEffect(() => {
    return () => {
      isUnmounted.current = true;
      if (paymentPoll.current) clearInterval(paymentPoll.current);
      // So an in-flight tick fails the stale-tick check.
      paymentPoll.current = undefined;
    };
  }, []);

  const [getPayments] = useLazyQuery<Query, QueryGetPaymentsPaginatedArgs>(GET_PAYMENTS_PAGINATED);

  const [createConfirmedPayment] = useMutation<Mutation, MutationCreatePaymentIntentArgs>(
    CREATE_CONFIRMED_PAYMENT_INTENT
  );

  const onSuccessfulFinish = async (setSubmitting) => {
    showSuccessAlert(dispatch, "Payment Charge Success");
    try {
      await refetch({
        id: invoiceId,
      });
    } catch (e) {
      showWarningAlert(
        dispatch,
        "Payment went through, but the invoice couldn't refresh. Reload it before taking another payment."
      );
    }
    setSubmitting(false);
    onClose();
  };

  const cleanedAmount = (val) => (val.toString().includes(".") ? val.toString() : val + ".00");

  const handleSinglePayment = async (values: PaymentValues[], sendReceipt: boolean, index: number, setSubmitting) => {
    const payment = values[index];
    let result;
    try {
      if (payment.paymentMethod === "Cash" || payment.paymentMethod === "Check") {
        result = await tryCashCheckPayment(payment, sendReceipt, index);
      } else {
        result = await tryCardPayment(payment, sendReceipt, index);
      }
    } catch (error) {
      // Proactive tax gate: tax not confirmed / corrected / TaxJar down. Surface the
      // specific reason, pull the (possibly corrected) totals back into the form, and
      // stop — do NOT fall through to the generic "Payment Charge Failed" path.
      const taxGate = parseTaxGateError(error);
      if (taxGate) {
        let refreshed = true;
        try {
          await refetch({ id: invoiceId });
        } catch (e) {
          refreshed = false;
        }
        // After part of a split went through, the refreshed balance only covers what's left, so the rows no longer
        // add up to it; close (like Cancel) so the dialog reopens on the real remaining balance.
        const closeOnRemainingBalance = refreshed && successfulPaymentsRef.current.length > 0;
        const message = closeOnRemainingBalance
          ? `${taxGateMessage(
              taxGate
            )} Part of this payment already went through. Reopen Get Paid for the remaining balance.`
          : taxGateMessage(taxGate);
        if (taxGate.outcome === TaxGateOutcome.CORRECTED) {
          showWarningAlert(dispatch, message);
        } else {
          showErrorAlert(dispatch, message);
        }
        if (closeOnRemainingBalance) {
          onClose();
          return;
        }
        if (!refreshed && (taxGate.outcome === TaxGateOutcome.CORRECTED || taxGate.amountChanged)) {
          if (!isUnmounted.current) setNeedsBalanceRefresh(true);
        }
        setSubmitting(false);
        return;
      }
      if (isNetworkFailure(error)) {
        // It may have been charged/recorded: don't report a failure (that invited Cash on top of it);
        // keep the form submitting and require an invoice refresh.
        if (!isUnmounted.current) setPaymentUnknown(true);
        return;
      }
      result = error;
      Sentry.captureException(propOr("", "message", result));
    }
    validateStatus({ payment: result, index, setSubmitting, values, sendReceipt, inInterval: false });
  };

  // Unlocks on any successful refresh, even if the balance didn't change, so this can't loop; no automatic retries.
  const refreshBalance = async () => {
    setIsRefreshingBalance(true);
    try {
      await refetch({ id: invoiceId });
    } catch (e) {
      showErrorAlert(dispatch, "Still offline. Reconnect, then tap Refresh balance again.");
      if (!isUnmounted.current) setIsRefreshingBalance(false);
      return;
    }
    if (isUnmounted.current) return;
    // Part of a split already went through: the refreshed balance only covers what's left, so the rows no longer
    // add up to it. Close (like the tax-gate path and Cancel) so the dialog reopens on the real remaining balance.
    if (successfulPaymentsRef.current.length > 0) {
      showWarningAlert(
        dispatch,
        "Part of this payment already went through. Reopen Get Paid for the remaining balance."
      );
      onClose();
      return;
    }
    setIsRefreshingBalance(false);
    setNeedsBalanceRefresh(false);
  };

  const refreshAfterUnknownPayment = async () => {
    setIsRefreshingAfterUnknown(true);
    try {
      await refetch({ id: invoiceId });
    } catch (e) {
      showErrorAlert(dispatch, "Still offline. Reconnect, then tap Refresh invoice again.");
      setIsRefreshingAfterUnknown(false);
      return;
    }
    onClose();
  };

  const recheckUnconfirmedPayment = () => {
    const pending = unconfirmedPayment.current;
    if (!pending) return;
    unconfirmedPayment.current = undefined;
    setPaymentStillConfirming(false);
    const { paymentId, ...rest } = pending;
    validateStatus({ ...rest, payment: { id: paymentId, status: "processing" }, inInterval: false });
  };

  // Never reset mid-payment. After a partial split, close so the dialog reopens on the real remaining balance.
  const handleCancel = async (resetToCurrentBalance: () => void, paymentInProgress: boolean) => {
    if (paymentInProgress) {
      showWarningAlert(dispatch, "Payment is already being processed.");
      return;
    }
    if (needsBalanceRefresh) {
      // Resetting now would restore the pre-correction amounts.
      showWarningAlert(dispatch, "Refresh the balance first.");
      return;
    }
    if (successfulPaymentsRef.current.length > 0) {
      // onClose doesn't refetch, so refresh first; if that fails, stay open rather than reopen on a stale balance.
      setIsRefreshingBeforeClose(true);
      try {
        await refetch({ id: invoiceId });
      } catch (e) {
        setIsRefreshingBeforeClose(false);
        showErrorAlert(dispatch, "Couldn't refresh the remaining balance. Check your connection and tap Cancel again.");
        return;
      }
      showWarningAlert(
        dispatch,
        "Part of this payment already went through. Reopen Get Paid for the remaining balance."
      );
      onClose();
      return;
    }
    resetToCurrentBalance();
  };

  const onSubmit = async (values: PaymentValues[], sendReceipt: boolean, setSubmitting) => {
    setPaymentStillConfirming(false);
    // Only charge rows that haven't gone through yet.
    const next = nextUnpaidIndex(values);
    if (next === -1) {
      onSuccessfulFinish(setSubmitting);
      return;
    }
    handleSinglePayment(values, sendReceipt, next, setSubmitting);
  };

  // The job may not be cached (or its query failed); destructuring it unguarded crashed the render.
  const job = jobData?.getJob ?? lastLoadedJob.current;
  const jobUnavailable = !job && !jobLoading;

  // Handled now, so report it to keep it visible in Sentry.
  useEffect(() => {
    if (jobUnavailable && !jobFailureReported.current) {
      jobFailureReported.current = true;
      Sentry.captureException(jobError ?? new Error("AddPaymentForm: job unavailable"), {
        tags: { feature: "collect-payment" },
        extra: { jobId, invoiceId },
      });
    }
  }, [jobUnavailable, jobError, jobId, invoiceId]);

  // refetch() rejects and doesn't flip `loading`, so track the retry ourselves; failure is shown inline.
  const retryJob = async () => {
    setIsRetryingJob(true);
    setJobRetryFailed(false);
    try {
      await refetchJob();
    } catch (e) {
      setJobRetryFailed(true);
    } finally {
      setIsRetryingJob(false);
    }
  };

  if (!job) {
    return jobLoading ? (
      <div className="flex justify-center py-8">
        <CircularProgress />
      </div>
    ) : (
      <div className="flex flex-col gap-4">
        <Typography>Could not load the job details needed to collect payment.</Typography>
        {!isOnline && <Typography color="error">This phone is offline. Reconnect, then tap Retry.</Typography>}
        <Button
          size={"large"}
          variant="contained"
          color="primary"
          onClick={retryJob}
          disabled={isRetryingJob}
          className="w-full"
        >
          {isRetryingJob ? <CircularProgress size={24} /> : "Retry"}
        </Button>
        {jobRetryFailed && !isRetryingJob && (
          <Typography color="error">Still couldn&apos;t load the job. Check your connection and try again.</Typography>
        )}
        <CancelButton onClick={onClose} />
      </div>
    );
  }

  const { type, warrantyCheckReason, warrantyCheckTechReason, market } = job;
  const isWarrantyJob = type === "Warranty Check";
  const hasWarrantyCheckReason = !!warrantyCheckReason || !!warrantyCheckTechReason;
  const isJobTypeValid = (isWarrantyJob && hasWarrantyCheckReason) || !isWarrantyJob;
  const isDirectBilling = contactData?.billingType === "direct-billing";
  const normalizedInvoiceStatus = (status || "").toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  const isAlreadyReadyForBilling =
    normalizedInvoiceStatus === "ready for billing" ||
    (normalizedInvoiceStatus.includes("ready") && normalizedInvoiceStatus.includes("billing"));

  const handleReadyForBilling = async () => {
    setIsReadyForBillingLoading(true);
    try {
      await nestBackendClient.post(`invoices/${invoiceId}/ready-for-billing`, {});
      showSuccessAlert(dispatch, "Invoice marked as ready for billing");
      await refetch({
        id: invoiceId,
      });
      onClose();
    } catch (error) {
      showErrorAlert(
        dispatch,
        error?.response?.data?.message || error?.message || "Failed to mark invoice as ready for billing"
      );
    } finally {
      setIsReadyForBillingLoading(false);
    }
  };

  if (isDirectBilling) {
    return (
      <div className="flex flex-col gap-4">
        <Typography>
          This fleet contact is billed via invoice. Mark this job as Ready for Billing to close the job and set the
          invoice to Ready for Billing.
        </Typography>
        <CancelButton
          onClick={() => {
            onClose();
          }}
        />
        {!checkListsCreated ? (
          <div className={"pb-8"}>
            <div className={"pb-2"}>You must finish the inspection</div>
            <Button
              size={"large"}
              variant={"contained"}
              color={"primary"}
              onClick={openInspection}
              className={"w-full"}
            >
              {"Complete Inspection"}
            </Button>
          </div>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleReadyForBilling}
            disabled={!checkListsCreated || isReadyForBillingLoading || isAlreadyReadyForBilling}
            className="w-full"
          >
            {isReadyForBillingLoading ? "Processing..." : "Ready for Billing"}
          </Button>
        )}
      </div>
    );
  }

  return (
    <>
      {!checkListsCreated && !contactData?.fleet && (
        <div className={"pb-8"}>
          <div className={"pb-2"}>You must finish the inspection in order to collect payment.</div>
          <Button size={"large"} variant={"contained"} color={"primary"} onClick={openInspection} className={"w-full"}>
            {"Complete Inspection"}
          </Button>
        </div>
      )}
      {!isJobTypeValid && (
        <div className={"pb-8"}>
          <div className={"pb-2"}>You must add a Warranty Check Reason.</div>
          <Button
            size={"large"}
            variant={"contained"}
            color={"primary"}
            onClick={handleAddWarrantyCheckReason}
            className={"w-full"}
          >
            {"Add Warranty Check Reason"}
          </Button>
        </div>
      )}
      <Formik<AddPaymentFormValues>
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={(val, { setSubmitting }) => {
          onSubmit(val.payments, val.sendReceipt, setSubmitting);
        }}
      >
        {({ values, isSubmitting, isValid, resetForm, dirty, submitCount, handleSubmit, setSubmitting }) => {
          const amountSum = values.payments.reduce((acc, val) => acc.add(currency(val.amount)), currency(0));
          const amountDifference = amountSum.subtract(currency(balanceDue)); //negative if overcharge, positive if undercharge
          const amountsSumToAmountOwed = amountSum?.value === currency(balanceDue)?.value; //do the amounts add up correctly to the amount owed
          const amountReadOnly = values.payments.length < 2 || (submitCount > 0 && successfulPayments.length > 0); //amount field should be read only in 1 pass 1 fail situation
          // The chain charges rows as they were at Submit, so lock them while a payment is in flight.
          const paymentInProgress =
            isSubmitting || isPollingPayment || paymentStillConfirming || paymentUnknown || isRefreshingBeforeClose;
          return (
            <Form>
              <BalanceDueSync
                balanceDue={balanceDue}
                hold={paymentInProgress || needsBalanceRefresh || successfulPayments.length > 0}
              />
              <PaymentStateReporter
                inProgress={paymentInProgress}
                balanceMayBeStale={successfulPayments.length > 0 || needsBalanceRefresh}
                onChange={onPaymentStateChange}
              />
              <div className={`flex justify-between ${!isPartiallyPaid && "pb-4"}`}>
                <div>
                  Amount due{isPartiallyPaid && " initially"}: ${amount}
                </div>
                <Link href="#" onClick={() => contactRefetch()} className="!no-underline">
                  Refresh Cards
                </Link>
              </div>
              {isPartiallyPaid && (
                <div className="pb-4">
                  <div>Amount paid: ${amountPaid}</div>
                  <div>Remaining balance due: ${balanceDue}</div>
                </div>
              )}
              <PaymentMethodsForm
                payments={values.payments}
                balanceDue={balanceDue}
                amountDifference={amountDifference}
                amountReadOnly={amountReadOnly}
                setAddNewCardState={setAddNewCardState}
                setBuyNowPayLaterState={setBuyNowPayLaterState}
                successfulPayments={successfulPayments}
                cardOptions={cardOptions}
                isSubmitting={isSubmitting || isPollingPayment}
                locked={paymentInProgress || needsBalanceRefresh}
                submitCount={submitCount}
                initialPaymentValues={initialValues.payments[0]}
                invoiceId={invoiceId}
              />
              <AddPaymentOptionsDialog
                market={market ?? undefined}
                contact={contactData}
                open={addNewCardState.showDialog}
                onClose={() => setAddNewCardState({ showDialog: false, setPaymentMethod: null })}
                setPaymentMethod={(paymentMethodId: string) => addNewCardState.setPaymentMethod?.(paymentMethodId)}
              />
              <BuyNowPayLaterDialog
                invoiceId={invoiceId}
                open={buyNowPayLaterState.showDialog}
                onClose={() =>
                  setBuyNowPayLaterState((prevState) => ({ ...prevState, setPaymentMethod: null, showDialog: false }))
                }
                buyNowPayLaterChosen={() => {
                  setBuyNowPayLaterState((prevState) => ({
                    ...prevState,
                    buyNowPayLaterSent: true,
                  }));
                  buyNowPayLaterState.setPaymentMethod?.();
                }}
              />
              <AreYouSureDialog
                onNo={() => {
                  setIsConfirmationOpen(false);
                  setSubmitting(false);
                }}
                onYes={() => {
                  setIsConfirmationOpen(false);
                  handleSubmit();
                }}
                yesButtonType="submit"
                title={`Are you sure you want to proceed with this payment?`}
                open={isConfirmationOpen}
                onClose={() => {
                  setIsConfirmationOpen(false);
                  setSubmitting(false);
                }}
              />
              {values.payments[0]?.paymentMethod === "buyNowPayLater" && !!buyNowPayLaterState.buyNowPayLaterSent ? (
                <div>
                  <div>Awaiting customer payment</div>
                  <Link
                    href="#"
                    onClick={async () => {
                      setRefetchProgress("Refreshing");
                      await refetch({ id: invoiceId });
                      setRefetchProgress("Refresh Done");
                    }}
                    className="!no-underline"
                  >
                    Refresh
                  </Link>
                  <div>{refetchProgress}</div>
                </div>
              ) : (
                <div>
                  {values.payments[0]?.paymentMethod !== "copyCheckoutLink" && (
                    <CheckboxField
                      name={"sendReceipt"}
                      label={"Send Receipt?"}
                      disabled={paymentInProgress || needsBalanceRefresh}
                    />
                  )}
                  <div className="mt-4 mb-8">
                    <CancelButton
                      // Reset to the current balance, not the one the dialog opened with.
                      onClick={() => handleCancel(() => resetForm({ values: initialValues }), paymentInProgress)}
                    />
                  </div>
                  {paymentUnknown ? (
                    <>
                      <Typography color="error" className="pb-4">
                        Lost connection before this payment was confirmed. It may have gone through, so don&apos;t take
                        another payment until the invoice is refreshed.
                      </Typography>
                      <Button
                        size={"large"}
                        variant={"contained"}
                        color={"primary"}
                        onClick={refreshAfterUnknownPayment}
                        disabled={isRefreshingAfterUnknown}
                        className={"w-full"}
                      >
                        {isRefreshingAfterUnknown ? <CircularProgress size={24} /> : "Refresh invoice"}
                      </Button>
                    </>
                  ) : needsBalanceRefresh ? (
                    <>
                      <Typography color="error" className="pb-4">
                        The total changed after a tax correction, but the new amount couldn&apos;t load. Refresh the
                        balance before taking payment.
                      </Typography>
                      <Button
                        size={"large"}
                        variant={"contained"}
                        color={"primary"}
                        onClick={refreshBalance}
                        disabled={isRefreshingBalance}
                        className={"w-full"}
                      >
                        {isRefreshingBalance ? <CircularProgress size={24} /> : "Refresh balance"}
                      </Button>
                    </>
                  ) : paymentStillConfirming ? (
                    <>
                      <Typography color="error" className="pb-4">
                        Still waiting for the card processor to confirm this payment. The customer may already have been
                        charged, so don&apos;t take another payment until it settles.
                      </Typography>
                      <Button
                        size={"large"}
                        variant={"contained"}
                        color={"primary"}
                        onClick={recheckUnconfirmedPayment}
                        className={"w-full"}
                      >
                        Check payment status
                      </Button>
                    </>
                  ) : (
                    <SubmitButton
                      isSubmitting={paymentInProgress}
                      isValid={
                        isValid &&
                        dirty &&
                        isJobTypeValid &&
                        (checkListsCreated || !!contactData?.fleet) &&
                        amountsSumToAmountOwed
                      }
                      type="button"
                      handleSubmit={() => {
                        if (values.payments[0]?.paymentMethod === "copyCheckoutLink") {
                          copyCheckoutLink();
                          return;
                        }
                        setIsConfirmationOpen(true);
                      }}
                    />
                  )}
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
